import { z } from 'zod'
import { apiService } from './api'
import { INTEGRATION_ENDPOINTS } from '../../api/endpoints'

// Schema definitions
const IntegrationSettingsSchema = z.object({
  id: z.string(),
  integrationType: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  config: z.record(z.string(), z.unknown()),
  isActive: z.boolean(),
  isConfigured: z.boolean(),
  lastTestedAt: z.string().nullable(),
  lastTestStatus: z.string().nullable(),
  lastTestMessage: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  updatedById: z.string().nullable(),
})

const IntegrationListResponseSchema = z.object({
  data: z.array(IntegrationSettingsSchema),
})

const IntegrationResponseSchema = z.object({
  data: IntegrationSettingsSchema.nullable(),
  message: z.string().optional(),
})

const TestResponseSchema = z.object({
  code: z.number(),
  message: z.string(),
  data: z.object({
    status: z.string(),
  }),
})

// Type exports (inferred from schemas)
export type IntegrationSettings = z.infer<typeof IntegrationSettingsSchema>
export type IntegrationListResponse = z.infer<typeof IntegrationListResponseSchema>
export type IntegrationResponse = z.infer<typeof IntegrationResponseSchema>
export type TestResponse = z.infer<typeof TestResponseSchema>

export interface SharePointConfig {
  spTenantId: string
  spClientId: string
  spCertThumbprint: string
  spPrivateKeyPem?: string
  spHost: string
  spSitePath: string
  spPiketnummersPath?: string
  spHulpdientsenPath?: string
  spBetrokkenPartijenPath?: string
  spCrisiscommunicatiePath?: string
}

export interface CoostoConfig {
  apiKey: string
  baseUrl: string
}

const EntraTrustedGroupMappingSchema = z.object({
  id: z.string(),
  name: z.string(),
  groupObjectId: z.string(),
  roleSlug: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  updatedById: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

const EntraTrustedTenantSchema = z.object({
  id: z.string(),
  name: z.string(),
  tenantId: z.string(),
  description: z.string().nullable(),
  isActive: z.boolean(),
  allowRoleClaims: z.boolean(),
  updatedById: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

const EntraTrustedGroupMappingListResponseSchema = z.object({
  data: z.array(EntraTrustedGroupMappingSchema),
})

const EntraTrustedTenantListResponseSchema = z.object({
  data: z.array(EntraTrustedTenantSchema),
})

const EntraTrustedGroupMappingResponseSchema = z.object({
  data: EntraTrustedGroupMappingSchema,
  message: z.string().optional(),
})

const EntraTrustedTenantResponseSchema = z.object({
  data: EntraTrustedTenantSchema,
  message: z.string().optional(),
})

export type EntraTrustedGroupMapping = z.infer<typeof EntraTrustedGroupMappingSchema>
export type EntraTrustedTenant = z.infer<typeof EntraTrustedTenantSchema>

export interface EntraTrustedGroupMappingInput {
  name: string
  groupObjectId: string
  roleSlug: string
  description?: string | null
  isActive?: boolean
}

export interface EntraTrustedTenantInput {
  name: string
  tenantId: string
  description?: string | null
  isActive?: boolean
  allowRoleClaims?: boolean
}

// Service class
export class IntegrationService {
  async listIntegrations(): Promise<IntegrationSettings[]> {
    const response = await apiService.get(
      INTEGRATION_ENDPOINTS.LIST,
      IntegrationListResponseSchema
    )
    return response.data
  }

  async getIntegration(type: string, includeSensitive = false): Promise<IntegrationSettings | null> {
    const url = includeSensitive
      ? `${INTEGRATION_ENDPOINTS.BY_TYPE(type)}?include_sensitive=true`
      : INTEGRATION_ENDPOINTS.BY_TYPE(type)

    const response = await apiService.get(url, IntegrationResponseSchema)
    return response.data
  }

  async configureSharePoint(config: SharePointConfig): Promise<IntegrationSettings> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.SHAREPOINT,
      config,
      undefined,
      IntegrationResponseSchema
    )
    if (!response.data) {
      throw new Error('No data returned from SharePoint configuration')
    }
    return response.data
  }

  async uploadSharePointKey(file: File): Promise<IntegrationSettings> {
    console.debug('[IntegrationService] Uploading SharePoint key, file:', file.name, file.size, 'bytes')

    const formData = new FormData()
    formData.append('file', file)

    // Log FormData contents for debugging
    console.debug('[IntegrationService] FormData entries:')
    for (const [key, value] of formData.entries()) {
      console.debug(`  ${key}:`, value instanceof File ? `File(${value.name})` : value)
    }

    // Use apiService.post which handles authentication automatically
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.SHAREPOINT_UPLOAD_KEY,
      formData,
      undefined,
      IntegrationResponseSchema
    )
    console.debug('[IntegrationService] Upload successful:', response)
    if (!response.data) {
      throw new Error('No data returned from SharePoint key upload')
    }
    return response.data
  }

  async configureCoosto(config: CoostoConfig): Promise<IntegrationSettings> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.COOSTO,
      config,
      undefined,
      IntegrationResponseSchema
    )
    if (!response.data) {
      throw new Error('No data returned from Coosto configuration')
    }
    return response.data
  }

  async toggleIntegration(type: string, isActive: boolean): Promise<IntegrationSettings> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.TOGGLE(type),
      { isActive },
      undefined,
      IntegrationResponseSchema
    )
    if (!response.data) {
      throw new Error('No data returned from toggle integration')
    }
    return response.data
  }

  async testIntegration(type: string): Promise<TestResponse> {
    return apiService.post(
      INTEGRATION_ENDPOINTS.TEST(type),
      {},
      undefined,
      TestResponseSchema
    )
  }

  async deleteIntegration(type: string): Promise<void> {
    const schema = z.object({
      data: z.object({
        deleted: z.boolean(),
      }),
    })

    await apiService.delete(INTEGRATION_ENDPOINTS.BY_TYPE(type), schema)
  }

  async listEntraTrustedGroupMappings(): Promise<EntraTrustedGroupMapping[]> {
    const response = await apiService.get(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_GROUP_MAPPINGS,
      EntraTrustedGroupMappingListResponseSchema
    )
    return response.data
  }

  async createEntraTrustedGroupMapping(
    payload: EntraTrustedGroupMappingInput
  ): Promise<EntraTrustedGroupMapping> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_GROUP_MAPPINGS,
      payload,
      undefined,
      EntraTrustedGroupMappingResponseSchema
    )
    return response.data
  }

  async updateEntraTrustedGroupMapping(
    id: string,
    payload: EntraTrustedGroupMappingInput
  ): Promise<EntraTrustedGroupMapping> {
    const response = await apiService.put(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_GROUP_MAPPING_BY_ID(id),
      payload,
      EntraTrustedGroupMappingResponseSchema
    )
    return response.data
  }

  async toggleEntraTrustedGroupMapping(
    id: string,
    isActive: boolean
  ): Promise<EntraTrustedGroupMapping> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_GROUP_MAPPING_TOGGLE(id),
      { isActive },
      undefined,
      EntraTrustedGroupMappingResponseSchema
    )
    return response.data
  }

  async deleteEntraTrustedGroupMapping(id: string): Promise<void> {
    await apiService.delete(INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_GROUP_MAPPING_BY_ID(id))
  }

  async listEntraTrustedTenants(): Promise<EntraTrustedTenant[]> {
    const response = await apiService.get(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_TENANTS,
      EntraTrustedTenantListResponseSchema
    )
    return response.data
  }

  async createEntraTrustedTenant(
    payload: EntraTrustedTenantInput
  ): Promise<EntraTrustedTenant> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_TENANTS,
      payload,
      undefined,
      EntraTrustedTenantResponseSchema
    )
    return response.data
  }

  async updateEntraTrustedTenant(
    id: string,
    payload: EntraTrustedTenantInput
  ): Promise<EntraTrustedTenant> {
    const response = await apiService.put(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_TENANT_BY_ID(id),
      payload,
      EntraTrustedTenantResponseSchema
    )
    return response.data
  }

  async toggleEntraTrustedTenant(id: string, isActive: boolean): Promise<EntraTrustedTenant> {
    const response = await apiService.post(
      INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_TENANT_TOGGLE(id),
      { isActive },
      undefined,
      EntraTrustedTenantResponseSchema
    )
    return response.data
  }

  async deleteEntraTrustedTenant(id: string): Promise<void> {
    await apiService.delete(INTEGRATION_ENDPOINTS.ENTRA_TRUSTED_TENANT_BY_ID(id))
  }
}

export const integrationService = new IntegrationService()
